import type { EarningDetail, ServiceLine } from '../healthcare-rates/types';

/** Numeric wire values — mirrors EPN.CustomPayRun.Api's own enums exactly (no
 * JsonStringEnumConverter anywhere in EPN, see docs/staging-data.md). Only the two run types with a
 * confirmed legacy code path (parity matrix §3) — do not add more without new confirmed research. */
export const CustomPayRunType = { Regular: 1, Bonus: 2 } as const;
export const customPayRunTypeLabels: Record<number, string> = { 1: 'Regular', 2: 'Bonus' };

/** EPN-242: longest memo the server accepts (characters, after trimming). The memo is stored in the run's `description` field. */
export const MEMO_MAX_LENGTH = 500;

export interface CustomPayRunResponse {
  id: string;
  companyId: string;
  runType: number;
  payScheduleId: string | null;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  description: string | null;
  stateCode: string;
  status: string;
  payrollRunId: string | null;
  payrollRunStatus: string | null;
  createdByUserId: string;
  createdByEmailSnapshot: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  duplicateWarnings: string[];
}

export interface CreateCustomPayRunInput {
  runType: number;
  payScheduleId: string | null;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  description: string | null;
  stateCode: string | null;
  idempotencyKey: string;
}

export interface EmployeeSelectionResponse {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  employmentStatus: string;
  isIncluded: boolean;
  acknowledgedStatusWarning: boolean;
  requiresAcknowledgment: boolean;
}

export interface EmployeeSelectionInput {
  employeeId: string;
  isIncluded: boolean;
  acknowledgeStatusWarning: boolean;
}

export interface EarningLineResponse {
  id: string;
  employeeId: string;
  earningCodeId: string;
  label: string;
  hours: number | null;
  rate: number | null;
  flatAmount: number | null;
  /** Healthcare: a line priced from the rate schedule (discipline, service type, pay basis, quantity). Absent on every other line. When present,
   * hours, rate and flatAmount are all null: the server resolves the rate. The server's discipline and service-type snapshots ride on it. */
  serviceLine?: ServiceLine | null;
}

export interface EarningLineInput {
  earningCodeId: string;
  label: string;
  hours: number | null;
  rate: number | null;
  flatAmount: number | null;
  /** Healthcare: see EarningLineResponse. A rate is never sent with a service line (the server answers 400 RATE_NOT_CLIENT_SUPPLIED). */
  serviceLine?: ServiceLine | null;
}

export interface DeductionElectionWithOptOutResponse {
  employeeId: string;
  deductionElectionId: string;
  deductionCodeId: string;
  amountOverride: number | null;
  isOptedOut: boolean;
  reason: string | null;
}

export interface DeductionOptOutInput {
  deductionElectionId: string;
  isOptedOut: boolean;
  reason: string | null;
}

export interface EmployeeCalculationResponse {
  employeeId: string;
  payrollCalculationId: string | null;
  netPayAmount: number;
  exceptionFlags: string | null;
}

export interface CalculateResponse {
  status: string;
  calculations: EmployeeCalculationResponse[];
}

/** EPN-190: one tax line behind an employee's totals. */
export interface PreviewTaxLineResponse {
  description: string;
  isEmployerPaid: boolean;
  amount: number;
  /** A decimal fraction (0.0765 = 7.65%) when the source gives one, otherwise null. */
  rate: number | null;
  taxTypeCode: string | null;
}

export interface PreviewEmployeeLineResponse {
  employeeId: string;
  firstName: string;
  lastName: string;
  payrollCalculationId: string | null;
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  netPay: number;
  exceptionFlags: string | null;
  /** EPN-190: absent on responses from a server that predates the breakdown. */
  taxableWages?: number;
  preTaxDeductionsTotal?: number;
  postTaxDeductionsTotal?: number;
  garnishmentsTotal?: number;
  taxLines?: PreviewTaxLineResponse[] | null;
  /** EPN-240: the employee's current pay method as Employees records it ("Check", "DirectDeposit"), or null when none is recorded. Display only. */
  payMethod?: string | null;
  /** EPN-240: true when this employee would be paid by this run but has no USABLE pay method (Check, or Direct Deposit with an approved,
   * payable bank account). Never acknowledgeable: they must be removed from the run, or their pay method fixed, first. */
  payMethodBlocked?: boolean;
  payMethodMessage?: string | null;
  /** Healthcare: how each earning line was priced (rate, rate source and a plain-language explanation). Absent or empty on a response that predates it. */
  earningDetails?: EarningDetail[];
  /** Healthcare: plain sentences the calculation recorded (for example an hourly base pay and an hourly service line in one calculation). */
  warnings?: string[];
}

export interface PayrollPreviewResponse {
  customPayRunId: string;
  status: string;
  runType: number;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  shadowCalculationWarning: string;
  companyGrossEarnings: number;
  companyEmployeeTaxes: number;
  companyEmployerTaxes: number;
  companyNetPay: number;
  employees: PreviewEmployeeLineResponse[];
  createdByUserId: string;
  createdByEmailSnapshot: string;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface SubmitForReviewResponse {
  customPayRunId: string;
  payrollRunId: string;
  payrollRunStatus: string;
}

/** EPN-191: result of the SIMULATED finalize. `isSimulation` is always true — no money moves.
 * `alreadyFinalized` is true when an existing payment batch was surfaced instead of a new one being initiated. */
export interface FinalizeForPaymentResponse {
  customPayRunId: string;
  payrollRunId: string;
  paymentBatchId: string;
  paymentBatchStatus: string;
  isSimulation: boolean;
  alreadyFinalized: boolean;
}

export interface StagingBlockedResponse {
  code: string;
  feature: string;
  detail: string;
}

/** The approved 4-step flow (Figma: Custom Payroll → Review Payroll → Pending Payroll → Approved Payroll). The step
 * keys are route segments: /payroll-processing/:runId/:step. */
export const WIZARD_STEPS = [
  { key: 'custom-payroll', label: 'Custom Payroll' },
  { key: 'review-payroll', label: 'Review Payroll' },
  { key: 'pending-payroll', label: 'Pending Payroll' },
  { key: 'approved-payroll', label: 'Approved Payroll' },
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number]['key'];

/** The stepper label of a step. The third step is dynamic: "Pending Payroll" for the person who prepared/submitted the run,
 * "Approve Payroll" for another user who can approve it. */
export function stepLabel(step: { key: WizardStepKey; label: string }, approverView: boolean): string {
  return step.key === 'pending-payroll' && approverView ? 'Approve Payroll' : step.label;
}

/** Route segments of the earlier 8-step wizard. Saved deep links keep working: each opens the step that replaced it. */
export const LEGACY_STEP_KEYS: Record<string, WizardStepKey> = {
  'run-details': 'custom-payroll',
  employees: 'custom-payroll',
  earnings: 'custom-payroll',
  deductions: 'custom-payroll',
  calculate: 'review-payroll',
  exceptions: 'review-payroll',
  preview: 'review-payroll',
  approval: 'pending-payroll',
};

/** EPN-240: how the other run matches this one. Identical period and pay day is the strongest. */
export type DuplicateMatch = 'IdenticalPeriodAndCheckDate' | 'OverlappingPeriod' | 'SameCheckDate';

/** EPN-240: one employee who is also in another live run (another Custom Pay Run, or a Payroll Review run) for the same period or pay day. */
export interface DuplicateFlag {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  otherRunKind: 'CustomPayRun' | 'PayrollReview';
  otherRunId: string;
  /** "Regular" or "Bonus" for a Custom Pay Run; null for a Payroll Review run. */
  otherRunType: string | null;
  otherRunStatus: string;
  otherPeriodStartDate: string;
  otherPeriodEndDate: string;
  otherCheckDate: string;
  match: DuplicateMatch;
  otherRunIsApproved: boolean;
}

/** EPN-240: the preparer's acknowledgement of one duplicate. A reason is required; `acknowledgesApprovedRun` must be true when the other run is Approved. */
export interface DuplicateAcknowledgementInput {
  employeeId: string;
  otherRunId: string;
  reason: string;
  acknowledgesApprovedRun: boolean;
}

/** EPN-240: an acknowledgement as recorded in the run's audit trail, shown to the approver. */
export interface DuplicateAcknowledgementResponse {
  employeeId: string;
  employeeName: string;
  otherRunKind: string;
  otherRunId: string;
  otherRunStatus: string;
  match: string;
  otherRunIsApproved: boolean;
  acknowledgesApprovedRun: boolean;
  reason: string;
  acknowledgedByUserId: string;
  acknowledgedByEmail: string | null;
  acknowledgedAtUtc: string;
}

export interface DownloadedCsv {
  blob: Blob;
  fileName: string;
  /** The text, for tests and previews. */
  text?: string;
}

/** Plain-language names for the run's own statuses, so a raw status name never reaches the screen. */
const RUN_STATUS_LABELS: Record<string, string> = {
  Draft: 'Draft',
  ValidationRequired: 'Needs employees',
  ReadyToCalculate: 'Ready to calculate',
  Calculated: 'Calculated',
  ExceptionsPresent: 'Calculated, with exceptions to review',
  ReadyForReview: 'Ready to send for approval',
  HandedOffToPayrollReview: 'Sent for approval',
  Discarded: 'Discarded',
  Withdrawn: 'Withdrawn by the preparer',
};

export function runStatusLabel(status: string): string {
  return RUN_STATUS_LABELS[status] ?? status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export const RUN_DISCARDED = 'Discarded';
/** EPN-241: the preparer took the run back after sending it, while Payroll Review still held it pending. Read-only and final, like Discarded. */
export const RUN_WITHDRAWN = 'Withdrawn';

/** EPN-241: the landing page's duplicate question before a run exists. `runWarnings` = same-type runs for the exact same period and pay day. */
export interface DuplicatePreviewResponse {
  runWarnings: string[];
  flags: DuplicateFlag[];
}

export interface DuplicatePreviewInput {
  runType: number;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  employeeIds: string[];
}

/** "Regular run" / "Bonus run": the label shown in the wizard header, the history list and the approver's view. */
export function runTypeLabel(runType: number): string {
  return runType === 2 ? 'Bonus run' : 'Regular run';
}
